import React from "react";

/* --- a small markdown renderer ---------------------------------------------
   Articles are written by a language model and stored as markdown. Rendering
   them means turning that text into a page, and the usual shortcut —
   dangerouslySetInnerHTML with a converted string — would make every article a
   place where a stray <script> could land.

   So this builds React elements instead. It is not a full CommonMark
   implementation; it covers what an article actually uses: headings,
   paragraphs, lists, quotes, code, links, bold and italic. Anything it does
   not recognise renders as the literal text that was written, which is the
   right failure: a visible stray asterisk, never an executed tag. */

const INLINE = /(\*\*[^*]+\*\*|\*[^*]+\*|_[^_]+_|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;

/** Only links we would follow ourselves. A markdown link is allowed to say
    anything, including `javascript:`; this is where that stops. */
const safeHref = (href) => {
  const value = (href || "").trim();
  if (/^(https?:\/\/|mailto:|\/|#)/i.test(value)) return value;
  return "";
};

function inline(text, keyPrefix) {
  const parts = String(text).split(INLINE).filter((part) => part !== "");
  return parts.map((part, index) => {
    const key = `${keyPrefix}-${index}`;
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={key}>{part.slice(2, -2)}</strong>;
    }
    if ((part.startsWith("*") && part.endsWith("*")) || (part.startsWith("_") && part.endsWith("_"))) {
      return <em key={key}>{part.slice(1, -1)}</em>;
    }
    if (part.startsWith("`") && part.endsWith("`")) {
      return (
        <code
          key={key}
          className="font-num rounded px-1 py-0.5 text-[0.9em]"
          style={{ backgroundColor: "var(--paper-2)" }}
        >
          {part.slice(1, -1)}
        </code>
      );
    }
    const link = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (link) {
      const href = safeHref(link[2]);
      if (!href) return <span key={key}>{link[1]}</span>;
      const external = /^https?:/i.test(href);
      return (
        <a
          key={key}
          href={href}
          className="link-underline"
          {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
        >
          {link[1]}
        </a>
      );
    }
    return <span key={key}>{part}</span>;
  });
}

export const Markdown = ({ source = "", className = "" }) => {
  const lines = String(source).replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let list = null;
  let quote = null;
  let fence = null;
  let paragraph = [];

  const flushParagraph = () => {
    if (!paragraph.length) return;
    blocks.push({ kind: "p", text: paragraph.join(" ") });
    paragraph = [];
  };
  const flushList = () => {
    if (!list) return;
    blocks.push(list);
    list = null;
  };
  const flushQuote = () => {
    if (!quote) return;
    blocks.push(quote);
    quote = null;
  };

  lines.forEach((raw) => {
    const line = raw.trimEnd();

    if (line.trim().startsWith("```")) {
      if (fence) {
        blocks.push({ kind: "code", text: fence.join("\n") });
        fence = null;
      } else {
        flushParagraph();
        flushList();
        flushQuote();
        fence = [];
      }
      return;
    }
    if (fence) {
      fence.push(raw);
      return;
    }

    if (!line.trim()) {
      flushParagraph();
      flushList();
      flushQuote();
      return;
    }

    const heading = line.match(/^(#{1,4})\s+(.*)$/);
    if (heading) {
      flushParagraph();
      flushList();
      flushQuote();
      blocks.push({ kind: `h${heading[1].length}`, text: heading[2] });
      return;
    }

    if (/^\s*([-*+]|\d+[.)])\s+/.test(line)) {
      flushParagraph();
      flushQuote();
      const ordered = /^\s*\d+[.)]\s+/.test(line);
      if (!list || list.ordered !== ordered) {
        flushList();
        list = { kind: "list", ordered, items: [] };
      }
      list.items.push(line.replace(/^\s*([-*+]|\d+[.)])\s+/, ""));
      return;
    }

    if (line.startsWith(">")) {
      flushParagraph();
      flushList();
      quote = quote || { kind: "quote", lines: [] };
      quote.lines.push(line.replace(/^>\s?/, ""));
      return;
    }

    if (/^\s*(---|\*\*\*|___)\s*$/.test(line)) {
      flushParagraph();
      flushList();
      flushQuote();
      blocks.push({ kind: "hr" });
      return;
    }

    flushList();
    flushQuote();
    paragraph.push(line.trim());
  });

  flushParagraph();
  flushList();
  flushQuote();
  if (fence) blocks.push({ kind: "code", text: fence.join("\n") });

  return (
    <div className={`prose-article ${className}`}>
      {blocks.map((block, index) => {
        const key = `b${index}`;
        switch (block.kind) {
          case "h1":
          case "h2":
            return (
              <h2 key={key} className="mt-10 text-[26px] font-semibold leading-tight sm:text-[30px]">
                {inline(block.text, key)}
              </h2>
            );
          case "h3":
            return (
              <h3 key={key} className="mt-8 text-[21px] font-semibold leading-tight">
                {inline(block.text, key)}
              </h3>
            );
          case "h4":
            return (
              <h4 key={key} className="mt-6 text-[15px] font-semibold uppercase tracking-[0.1em]">
                {inline(block.text, key)}
              </h4>
            );
          case "list":
            return block.ordered ? (
              <ol key={key} className="mt-4 list-decimal space-y-2 pl-5">
                {block.items.map((item, i) => (
                  <li key={`${key}-${i}`}>{inline(item, `${key}-${i}`)}</li>
                ))}
              </ol>
            ) : (
              <ul key={key} className="mt-4 list-disc space-y-2 pl-5">
                {block.items.map((item, i) => (
                  <li key={`${key}-${i}`}>{inline(item, `${key}-${i}`)}</li>
                ))}
              </ul>
            );
          case "quote":
            return (
              <blockquote
                key={key}
                className="my-6 border-l-2 pl-4 italic"
                style={{ borderColor: "var(--gild)", color: "var(--ink-soft)" }}
              >
                {inline(block.lines.join(" "), key)}
              </blockquote>
            );
          case "code":
            return (
              <pre
                key={key}
                className="font-num my-6 overflow-x-auto rounded-lg p-4 text-[12.5px]"
                style={{ backgroundColor: "var(--paper-2)" }}
              >
                <code>{block.text}</code>
              </pre>
            );
          case "hr":
            return (
              <hr key={key} className="my-8" style={{ borderColor: "var(--line)" }} />
            );
          default:
            return (
              <p key={key} className="mt-4 leading-[1.75]">
                {inline(block.text, key)}
              </p>
            );
        }
      })}
    </div>
  );
};

export default Markdown;
